<template>
    <dropdown :caret="false" :visible="show" as="div" class="dropdown mb-3 border-0">
        <template #toggler>
            <jet-input
                ref="input"
                v-model="form.search"
                :horizontal="false"
                :inline="true"
                :invalidFeedback="error"
                :label="label"
                :placeholder="placeholder"
                :required="required"
                @blur="closeModel"
                @focus="handleFocus"
                @keyup="search"
                @keyup.esc="blurInput"
                @click.native="handleClick"
            >
                <template #append>
                    <spinner v-if="form.processing || processing" />
                    <search v-else />
                </template>
            </jet-input>
        </template>
        <div v-if="assets.data?.length > 0" class="overflow-auto" style="max-height: 153px">
            <dropdown-item v-for="asset in assets.data" :key="asset.id" class="py-1" @click="select(asset)">{{ getLabel(asset) }} </dropdown-item>
        </div>
        <dropdown-item
            v-else-if="form.search && assets.data?.length === 0"
            :disabled="true"
            class="py-1"
            v-text="trans('assets.overview.results.empty')"
        />
        <dropdown-item v-else :disabled="true" class="!bg-white py-1 text-muted" v-text="placeholder" />

        <div v-if="assets.data?.length > 0" :class="{ 'add-shadow': assets.data?.length > 5 }" class="d-flex justify-content-end px-3">
            <small class="text-muted">{{ assets.meta?.total }}/{{ assets.meta?.all }}</small>
        </div>
    </dropdown>
</template>
<script setup>
import { ref } from 'vue';
import { debounce } from 'lodash';
import { useForm } from '@inertiajs/vue3';
import { $http } from '@/bootstrap.js';
import Dropdown from '@/Components/Dropdown.vue';
import DropdownItem from '@/Components/DropdownItem.vue';
import JetInput from '@/Jetstream/Input.vue';
import Search from '@/../icons/Search.vue';

defineOptions({
    name: 'asset-selection-field',
});

const emit = defineEmits(['selected']);

const props = defineProps({
    label: String,
    error: String,
    horizontal: Boolean,
    placeholder: String,
    required: {
        type: Boolean,
        default: false,
    },
    value: String,
    disabled: {
        type: Boolean,
        default: false,
    },
    searchUrl: String,
    processing: Boolean,
});

const show = ref(false);
const assets = ref({});
const form = useForm({ search: null });
const abortController = ref(null);
const input = ref(null);

const search = debounce((event) => {
    abortController.value?.abort();
    form.processing = false;

    // Handle escape
    if (event.keyCode === 27) {
        return;
    }

    // Escape keypress
    abortController.value = new AbortController();

    form.processing = true;

    $http
        .get(props.searchUrl, {
            signal: abortController.value.signal,
            params: { search: form.search },
        })
        .then((resp) => {
            const searchResults = resp.data;

            if (searchResults.meta.current_page === 1) {
                assets.value = Object.assign({}, assets.value, searchResults);
            } else {
                assets.value.links = searchResults.links;
                assets.value.meta = searchResults.meta;
                assets.value.data.push(...searchResults.data);
            }
        })
        .catch((e) => {})
        .finally(() => {
            form.processing = false;
            show.value = true;
        });
}, 300);

const getLabel = (asset) => {
    return [asset.type.shortLabel, asset.serialNumber, asset.location.airport, asset.location.name, asset.manufacturer.name]
        .filter((el) => !!el)
        .join('/');
};

const select = (asset) => {
    form.search = getLabel(asset);
    show.value = false;
    emit('selected', asset);
};

const handleClick = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
};

const handleFocus = (event) => {
    form.reset();
    form.clearErrors();
    show.value = true;
};

const closeModel = debounce(() => {
    show.value = false;
}, 150);

const blurInput = (event) => {
    input.value?.$el?.getElementsByTagName('input')[0]?.blur();
};

const setActive = (shouldActivate = true) => {
    if (shouldActivate) {
        input.value?.$el?.getElementsByTagName('input')[0]?.focus();
        show.value = true;
    } else {
        blurInput();
        show.value = false;
    }
};

defineExpose({
    setActive,
});
</script>
<style scoped>
:deep(.dropdown-menu) {
    width: 100%;
    overflow: hidden;
}

:deep(.dropdown-item:last-child) {
    border-radius: 0;
}

.dropdown.btn-group {
    display: block;
}
</style>
